<template>
  <div class="claim-note-header">
    <strong>
      {{ note.createdBy }} <span class="badge">{{ note.type }}</span>
    </strong>
    {{ formatTimestamp(note.creationTimestampUtc) }}
  </div>
  <div class="claim-note-body">
    <inline-edit-field
      :isEditable="isEditable"
      :editing="isEditMode"
      @save="onSave()"
      @cancel="onCancel()"
      @edit="onEdit()"
    >
      <template v-slot:read>
        {{ noteText }}
      </template>
      <template v-slot:edit>
        <textarea name="note-add" v-model="noteText" :placeholder="placeholder" ref="inputRef" />
      </template>
    </inline-edit-field>

    <div
      v-if="showChildren && visibleReplies && visibleReplies.length"
      class="claim-note-reply-list"
    >
      <label>Replies</label>
      <ul>
        <li v-for="reply of visibleReplies" :key="reply.id" class="claim-note">
          <claim-note-item
            :showChildren="false"
            :note="reply"
            :title="title"
            @save="onSaveReply"
            @cancel="onCancelReply"
            placeholder="Please enter your response here..."
          />
        </li>
      </ul>
      <button
        v-if="showChildren && displayShowAllBtn"
        class="white small"
        @click="onShowAllReplies"
      >
        Show all {{ title }}
      </button>
    </div>
    <div class="claim-note-action reply">
      <button v-if="showChildren && displayReplyBtn" class="white small" @click="onAddReply">
        Reply
      </button>
    </div>
  </div>
</template>

<script lang="ts">
import { computed, ComputedRef, defineComponent, nextTick, PropType, ref } from 'vue';

import InlineEditField from '@/components/InlineEditField.vue';
import { formatDisplayDateTime } from '@/utils';
import { IClaimNote, IClaimNoteOrReply, IClaimNoteReply, Nullable } from '@/types';
import { cloneDeep } from 'lodash';

export default defineComponent({
  name: 'ClaimNoteItem',
  components: {
    InlineEditField,
  },
  props: {
    note: {
      type: Object as PropType<IClaimNoteOrReply>,
      required: true,
    },
    placeholder: {
      type: String,
      default: 'Please enter your note here...',
    },
    showChildren: {
      type: Boolean,
      default: true,
    },
    title: {
      type: String,
      default: 'Notes',
    },
  },
  emits: ['save', 'cancel'],
  setup(props, { emit }) {
    const INITIAL_REPLIES_NUM = 2;

    const isEditable = computed(() => props.note.type !== 'SUPPLIER');

    // Automatically select Edit Mode if we don't have an ID (in other words, this is a new note/reply)
    const isEditMode = ref<boolean>(!props.note.id);

    const inputRef = ref<Nullable<HTMLElement>>(null);

    /**
     * Locally-editable copy of the Note/Reply text
     */
    const noteText = ref<string>(props.note.text);

    /**
     * Store a copy of the original Note/Reply text in case user presses Cancel
     */
    const originalValue = ref<string>(props.note.text);

    /**
     * Whether or not we are showing all replies, or just a limited preview
     */
    const expandReplies = ref<boolean>(false);

    /**
     * Placeholder to store the new reply that's being authored
     */
    const newReply = ref<Nullable<IClaimNoteReply>>(null);

    /**
     * Format a timestamp
     */
    function formatTimestamp(dateTime: number) {
      return formatDisplayDateTime(dateTime, 'MMM Do, YYYY - h:mm a');
    }

    /**
     * Triggered when user cancels editing the note they are currently editing.
     * We revert the value being displayed to the original text.
     */
    function onCancel(): void {
      isEditMode.value = false;
      noteText.value = originalValue.value;
      emit('cancel');
    }

    /**
     * Triggered when user saves the note they are currently editing.  If they made no
     * change, we consider that a "cancel".
     */
    function onSave(): void {
      isEditMode.value = false;
      if (originalValue.value !== noteText.value) {
        originalValue.value = noteText.value;
        const updatedNote: IClaimNoteOrReply = cloneDeep(props.note);
        updatedNote.text = noteText.value;
        emit('save', updatedNote);
      } else {
        onCancel();
      }
    }

    /**
     * Triggered when user starts editing a note.
     */
    async function onEdit(): Promise<void> {
      await nextTick();
      inputRef.value?.focus();
    }

    /**
     * Triggered when user presses "Add Reply".  All we do here is initialize
     * `newReply`, and `visibleReplies` takes care of the rest.
     */
    function onAddReply(): void {
      if (props.note.noteType === 'note' && props.note.id) {
        expandReplies.value = true;

        // Find the most recent `IClaimNote` in the thread
        const parentNote: IClaimNote =
          ((props.note.replies || []).filter(
            reply => reply.noteType === 'note'
          ) as IClaimNote[]).pop() || props.note;

        if (parentNote && parentNote.id) {
          newReply.value = {
            noteType: 'reply',
            type: 'PUBLIC',
            text: '',
            // eslint-disable-next-line @typescript-eslint/camelcase
            parent_id: parentNote.id,
          };
        }
      }
    }

    /**
     * Triggered when user saves a reply they are currently adding/editing
     * Also makes sure that we display all replies in case they just exceeded
     * `INITIAL_REPLIES_NUM` by adding a new reply.
     */
    function onSaveReply(value: IClaimNoteOrReply): void {
      emit('save', value);
      expandReplies.value = true;
      newReply.value = null;
    }

    /**
     * Triggered when user cancels editing a Reply.  Note that this is the second part
     * of this operation; the first happens in the child `ClaimNoteItem` component, and
     * is handled by the child component's `onCancel` handler.
     */
    function onCancelReply(): void {
      newReply.value = null;
    }

    const allReplies: ComputedRef<IClaimNoteOrReply[]> = computed(() => {
      return props.note.noteType === 'note' && props.showChildren ? props.note?.replies || [] : [];
    });

    const visibleReplies: ComputedRef<IClaimNoteOrReply[]> = computed(() => {
      const replies = [];
      if (expandReplies.value) {
        replies.push(...allReplies.value);
      } else {
        replies.push(...allReplies.value.slice(0, INITIAL_REPLIES_NUM));
      }
      if (newReply.value) {
        replies.push(newReply.value);
      }
      return replies;
    });

    /**
     * The "Show All" button is visible only when there are more replies than currently visible.
     */
    const displayShowAllBtn = computed<boolean>(
      () => visibleReplies.value.length < allReplies.value.length
    );

    const displayReplyBtn = computed<boolean>(
      () => !newReply.value && !!props.note?.id && !displayShowAllBtn.value
    );

    const onShowAllReplies = () => {
      expandReplies.value = true;
    };

    return {
      formatTimestamp,
      displayShowAllBtn,
      visibleReplies,
      noteText,
      isEditable,
      isEditMode,
      onSave,
      onCancel,
      onEdit,
      inputRef,
      displayReplyBtn,
      onAddReply,
      onSaveReply,
      onCancelReply,
      onShowAllReplies,
    };
  },
});
</script>

<style lang="scss" scoped>
.claim-note-header {
  font-size: var(--font-size-12);
  font-weight: bold;
  display: flex;
  justify-content: space-between;
  align-items: center;
  line-height: 1;
  margin-bottom: var(--space-2);
  color: var(--grey-ultra-dark);
}
</style>
